import {
  matchPath,
  matchRoutes,
  NavLink,
  NavLinkProps,
  useLocation,
} from 'react-router-dom';
import styled, { css } from 'styled-components';
import Routes from 'constants/Routes';
import RightHandNavBar from './RightHandNavBar';
import useAuthenticationState from 'contexts/authentication/useAuthentication';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useRideUpdates from 'pages/CasesWithRideUpdates/useRideUpdates';
import useRideWaitingList from 'pages/RideWaitingList/useRideWaitingList';
import { useDevMenuState } from 'components/DevMenu';
import useAbsenceRequests from 'pages/Employees/useUnhandledCount';
import useMe from 'contexts/authentication/useMe';
import Roles, {
  AccessAssignmentRequestCountEndpointPolicy,
  DefaultWebAdminPolicy,
  EmployeesPageAccessPolicy,
  includesRoles,
  InternalDeliveryGroupAccessPolicy,
  isAuthorized,
  isAvailabilityReadOnly,
  isStartklarOnly,
  KPIsAccessPolicy,
  ManagementReportAccessPolicy,
  QualityFlagsAccessPolicy,
  RiddermarkKPIsAccessPolicy,
} from 'constants/Roles';
import { useCaseGetCaseAssignmentRequestsCount } from 'api/case/case';
import WavingHand from 'components/WavingHand/WavingHand';
import { useAssignmentRequestsGetCounts } from 'api/assignment-requests/assignment-requests';
import { useReadLocalStorage } from 'usehooks-ts';
import { NavBarState, NavBarStateKey } from 'constants/AppConstants';
import Dropdown from 'components/Dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import TextButton from 'components/inputs/TextButton';
import { arraySpreadIf } from 'utils/spreading';
import useIsExcludedFromFullöDayPlanning from 'hooks/useHasTestAccessToPlaceholderCases';
import useTranslations from 'contexts/basicData/useTranslations';
import { CaseTypeEnum } from 'api/model';
import useHasTestAccessToCarTransportTrucks from 'hooks/useHasTestAccessToCarTransportTrucks';
import useHasAccessToSharebox from 'hooks/useHasAccessToSharebox';
import useHasAccessToRiddermarkReporting from 'hooks/useHasAccessToRiddermarkReporting';

// import useBetaFeatures from 'contexts/basicData/useBetaFeatures';

const navLinkClassName = 'nav-link';
const activeNavLinkClassName = 'active-nav-link';
const unAuthenticatedNavLinkClassName = 'unauthenticated-nav-link';

// zg-noprint plockas bort vid utskrift av ledningsrapportens stilmall. Klassen gör ingenting
// på övriga sidor — ingen regel träffar den där — men navigationen hör inte hemma på ett papper.
const NavBarContainer = styled.div.attrs({ className: 'zg-noprint' })`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 30px;
    padding: 10px 10px;

    border-bottom: 1px solid ${theme.colors.border.primary};
  `}
`;

const NavLinks = styled.div<{ direction?: 'row' | 'column' | undefined }>`
  ${({ theme, direction = 'row' }) => css`
    display: flex;
    flex-direction: ${direction};
    gap: 30px;
    align-items: baseline;

    .${navLinkClassName} {
      border: none;
      background-color: transparent;
      text-transform: capitalize;
      color: ${theme.colors.foreground.primary};
      text-decoration: none;

      &:hover {
        color: ${theme.colors.foreground.tint};
      }
    }

    .${unAuthenticatedNavLinkClassName} {
      border: none;
      background-color: transparent;
      text-transform: capitalize;
      color: ${theme.colors.foreground.tertiary};
      text-decoration: none;
      cursor: default;
    }

    .${activeNavLinkClassName} {
      padding-bottom: 1px;
      border-bottom: 2px solid ${theme.colors.background.button};
      font-weight: bold;
    }
  `}
`;

const DropdownNavLinks = styled(NavLinks)`
  ${({ theme }) => css`
    background-color: ${theme.colors.background.primary};
    border: 1px solid ${theme.colors.border.primary};
    padding: 10px;
    gap: 10px;
  `}
`;

interface NavBarDropDownProps {
  navItems: NavRule[];
  makeActiveClassname: (props: { isActive: boolean }) => string;
  /** Rubrik i dropdownen/knappen (t.ex. "Uppdragslistor" eller "Planering") och prefix när en
   * route är aktiv ("Uppdragslista: X" / "Planering: X"). Default = Uppdragslista-varianten. */
  label?: { header: string; activePrefix: string };
  /** Om dropdownen ska markera aktiv route (fetstil + "prefix: X"). Sätt false för sekundära
   * menyer vars routes redan speglas av en annan meny (t.ex. "Planering"), så att inte två
   * rubriker markeras samtidigt för en delad ärendetyp. Default true. */
  reflectActiveRoute?: boolean;
}

interface AuthorizedNavLinkProps extends NavLinkProps {
  allow?: number[];
  deny?: number[];
}

const AuthorizedNavLink: React.FC<AuthorizedNavLinkProps> = ({
  allow = [],
  deny = [],
  ...rest
}) => {
  const me = useMe();

  const show = useMemo((): boolean => {
    if (!me) return false;
    const roles = me?.roles ?? [];

    return isAuthorized(allow, deny, roles);
  }, [me, deny, allow]);

  return show ? <NavLink {...rest} style={{ whiteSpace: 'nowrap' }} /> : null;
};

const Img = styled.img`
  margin-top: 10px;
`;
type NavRule = {
  content: React.ReactNode;
  allow?: number[];
  deny?: number[];
  route: string;
};

const NavBarDropDown: React.FC<NavBarDropDownProps> = ({
  navItems,
  makeActiveClassname,
  label = { header: 'Uppdragslistor', activePrefix: 'Uppdragslista' },
  reflectActiveRoute = true,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const location = useLocation();

  const paths = navItems.map((rule) => ({ path: `${rule.route}/*` }));

  const routematch = useMemo(
    () => matchRoutes(paths, location.pathname),
    [paths, location.pathname]
  );

  const activeRoute = useMemo(() => {
    if (!reflectActiveRoute) return undefined;
    return navItems.find((rule) => {
      return rule.route === routematch?.[0].pathnameBase;
    });
  }, [navItems, routematch, reflectActiveRoute]);

  return (
    <Dropdown
      onLostFocus={() => setIsOpen(false)}
      content={
        isOpen && (
          <DropdownNavLinks direction="column" style={{ gap: 10 }}>
            <strong>{label.header}</strong>
            {navItems.map((rule) => (
              <AuthorizedNavLink
                key={rule.route}
                className={makeActiveClassname}
                to={rule.route}
                allow={rule.allow}
                deny={rule.deny}
                onClick={() => setIsOpen(false)}
              >
                {rule.content}
              </AuthorizedNavLink>
            ))}
          </DropdownNavLinks>
        )
      }
    >
      <NavLinks>
        {!activeRoute ? (
          <TextButton
            onClick={() => setIsOpen(!isOpen)}
            style={{
              whiteSpace: 'nowrap',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200%',
              gap: '5px',
              fontWeight: 'normal',
              color: 'black',
              padding: 2,
            }}
          >
            <span>{label.header}</span>
            <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} />
          </TextButton>
        ) : (
          <NavLink
            className={makeActiveClassname({ isActive: !!activeRoute })}
            to={activeRoute?.route ?? '/'}
            onClick={() => setIsOpen(!isOpen)}
            style={{
              whiteSpace: 'nowrap',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
            }}
          >
            <span>
              {label.activePrefix}: {activeRoute.content}
            </span>
            <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} />
          </NavLink>
        )}
      </NavLinks>
    </Dropdown>
  );
};

/** Fyller utrymmet mellan logotypen och högermenyn — det är DEN bredden Mer-menyn räknar med. */
const OverflowRow = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

/**
 * Osynlig kopia av hela raden. Här mäts varje länk, även de som just nu ligger i Mer-menyn —
 * annars går det inte att veta när de får plats igen. max-content så att kopian aldrig trycks
 * ihop av raden den ligger i.
 */
const MeasureRow = styled(NavLinks)`
  position: absolute;
  top: 0;
  left: 0;
  width: max-content;
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
`;

const MoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  font: inherit;
  white-space: nowrap;
  cursor: pointer;
`;

/** Expanderat läge: användaren har valt att se allt, så raden bryts i stället för att gömma. */
const WrappingNavLinks = styled(NavLinks)`
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
  row-gap: 8px;
`;

const isRouteActive = (route: string, pathname: string) =>
  matchPath({ path: route, end: false }, pathname) !== null;

interface OverflowNavLinksProps {
  rules: NavRule[];
  makeActiveClassname: (props: { isActive: boolean }) => string;
}

/**
 * Länkarna som får plats visas, resten hamnar under "Mer". Utan den här blev navbaren lika bred
 * som summan av alla länkar användaren har behörighet till — för en superadmin ~2600 px — och
 * hela appen fick sidscroll.
 *
 * Behörigheten filtreras FÖRE mätningen: AuthorizedNavLink renderar null för en otillåten länk,
 * och då hade index i mätraden och i den synliga raden pekat på olika länkar.
 */
const OverflowNavLinks: React.FC<OverflowNavLinksProps> = ({
  rules,
  makeActiveClassname,
}) => {
  const me = useMe();
  const location = useLocation();
  const visibleRules = useMemo(
    () =>
      me
        ? rules.filter((rule) =>
            isAuthorized(rule.allow ?? [], rule.deny ?? [], me.roles ?? [])
          )
        : [],
    [rules, me]
  );

  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fitCount, setFitCount] = useState(visibleRules.length);
  const [isOpen, setIsOpen] = useState(false);

  // Layout-effekt: antalet ska vara räknat innan raden målas, annars blinkar den för bred.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return undefined;

    const compute = () => {
      const elements = Array.from(measure.children) as HTMLElement[];
      // Sista barnet i mätraden är Mer-knappen.
      const moreWidth = elements.pop()?.getBoundingClientRect().width ?? 0;
      const widths = elements.map((el) => el.getBoundingClientRect().width);
      const gap = parseFloat(getComputedStyle(measure).columnGap) || 0;
      const available = row.getBoundingClientRect().width;

      const allWidth =
        widths.reduce((sum, w) => sum + w, 0) + gap * Math.max(widths.length - 1, 0);
      if (allWidth <= available) {
        setFitCount(widths.length);
        // Mer försvinner — stäng menyn, annars kom den tillbaka redan utfälld nästa gång raden
        // blev för trång (dropdownen avmonteras, men isOpen låg kvar).
        setIsOpen(false);
        return;
      }

      let used = moreWidth;
      let count = 0;
      for (const width of widths) {
        if (used + gap + width > available) break;
        used += gap + width;
        count += 1;
      }
      setFitCount(count);
    };

    compute();
    // Mätraden observeras också: dess bredd ändras när en räknare i en länk ändras.
    const observer = new ResizeObserver(compute);
    observer.observe(row);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [visibleRules]);

  const shownRules = visibleRules.slice(0, fitCount);
  const hiddenRules = visibleRules.slice(fitCount);
  // Ligger sidan man står på under Mer, markeras Mer — annars syns det inte var man är.
  const hiddenRuleIsActive = hiddenRules.some((rule) =>
    isRouteActive(rule.route, location.pathname)
  );

  return (
    <OverflowRow ref={rowRef}>
      <NavLinks>
        {shownRules.map((rule) => (
          <NavLink
            key={rule.route}
            className={makeActiveClassname}
            style={{ whiteSpace: 'nowrap' }}
            to={rule.route}
          >
            {rule.content}
          </NavLink>
        ))}
        {hiddenRules.length > 0 && (
          <Dropdown
            content={
              isOpen && (
                <DropdownNavLinks direction="column">
                  {hiddenRules.map((rule) => (
                    <NavLink
                      key={rule.route}
                      className={makeActiveClassname}
                      onClick={() => setIsOpen(false)}
                      style={{ whiteSpace: 'nowrap' }}
                      to={rule.route}
                    >
                      {rule.content}
                    </NavLink>
                  ))}
                </DropdownNavLinks>
              )
            }
            onLostFocus={() => setIsOpen(false)}
            position="left"
          >
            <MoreButton
              className={makeActiveClassname({ isActive: hiddenRuleIsActive })}
              onClick={() => setIsOpen((prev) => !prev)}
              type="button"
            >
              Mer
              <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} />
            </MoreButton>
          </Dropdown>
        )}
      </NavLinks>

      <MeasureRow aria-hidden ref={measureRef}>
        {visibleRules.map((rule) => (
          <span
            key={rule.route}
            className={makeActiveClassname({
              isActive: isRouteActive(rule.route, location.pathname),
            })}
            style={{ whiteSpace: 'nowrap' }}
          >
            {rule.content}
          </span>
        ))}
        <MoreButton
          className={makeActiveClassname({ isActive: true })}
          tabIndex={-1}
          type="button"
        >
          Mer
          <FontAwesomeIcon icon={faCaretDown} />
        </MoreButton>
      </MeasureRow>
    </OverflowRow>
  );
};

const NavBar: React.FC = () => {
  const hasAdminAccessToPlaceholderCases = useIsExcludedFromFullöDayPlanning();
  const hasTestAccessToCarTransportTrucks =
    useHasTestAccessToCarTransportTrucks();
  const {
    state: { isAuthenticated },
  } = useAuthenticationState();
  const me = useMe();
  // const [showFolded, setShowFolded] = useLocalStorage('showFolded', false);
  const navbarState = useReadLocalStorage<NavBarState>(NavBarStateKey);

  const [, setDevMenuState] = useDevMenuState();
  const { rideUpdatesCount } = useRideUpdates();
  const { waitingListCount: rideWaitingListCount } = useRideWaitingList();
  const hasAccessToSharebox = useHasAccessToSharebox();
  const hasAccessToRiddermarkReporting = useHasAccessToRiddermarkReporting();
  const { data: caseAssignmentRequestsCount } =
    useCaseGetCaseAssignmentRequestsCount({
      query: {
        refetchInterval: 10000,
        enabled:
          isAuthenticated &&
          me !== null &&
          includesRoles([Roles.Admin], me.roles),
      },
    });

  const { data: assignmentRequestsCountData } = useAssignmentRequestsGetCounts({
    query: {
      staleTime: 15000,
      refetchInterval: 60000,
      enabled:
        isAuthenticated &&
        me !== null &&
        includesRoles(
          AccessAssignmentRequestCountEndpointPolicy.allow,
          me.roles
        ),
    },
  });

  const { unhandledTotalCount } = useAbsenceRequests();
  const { caseTypes } = useTranslations();

  const assignmentNavItems: NavRule[] = useMemo(
    () => [
      {
        content: 'Alla',
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.search.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Purchase],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.purchases.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Auction],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.auction.index,
      },
      {
        content: caseTypes[CaseTypeEnum.RentedStaff],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.rentedStaff.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Samakning],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.samakning.index,
      },
      {
        content: caseTypes[CaseTypeEnum.HomeDelivery],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.homeDeliveries.index,
      },
      {
        content: caseTypes[CaseTypeEnum.InternalDelivery],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.internalDeliveries.index,
      },
      {
        content: `${caseTypes[CaseTypeEnum.FacilityToFacility]} (${caseTypes[CaseTypeEnum.InternalDeliverySales]})`,
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.internalDeliverySales.index,
      },
      {
        content: caseTypes[CaseTypeEnum.FacilityToFacility],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.facilityToFacility.index,
      },
      {
        content: caseTypes[CaseTypeEnum.FacilityTransfer],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.facilityTransfer.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Verkstadsbilar],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.verkstadsbilar.index,
      },
      {
        content: caseTypes[CaseTypeEnum.FaciliyExchange],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.facilityExchange.index,
      },
      {
        content: caseTypes[CaseTypeEnum.RMOther],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.rmOther.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Dackkorning],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.dackkorning.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Avyttringar],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.avyttringar.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Garantiarenden],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.garantiarenden.index,
      },
      {
        content: caseTypes[CaseTypeEnum.AterTillLogistik],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.aterTillLogistik.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Swamp],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.swamp.index,
      },
      {
        content: caseTypes[CaseTypeEnum.TestDrive],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.testDrives.index,
      },
      {
        content: 'Mellanlandade',
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.pendingAssignments.index,
      },
      {
        content: 'VIKTIGT',
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.viktigt.index,
      },
      {
        content: 'SKA PÅ LASTBIL',
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.skaPaLastbil.index,
      },
      ...arraySpreadIf(hasAdminAccessToPlaceholderCases, {
        content: 'Utfyllnadsuppdrag',
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.placeholder.index,
      }),
      ...arraySpreadIf(hasTestAccessToCarTransportTrucks, {
        content: caseTypes[CaseTypeEnum.CarTransportTruck],
        deny: [Roles.Riddermark],
        route: Routes.carTransportTrucks.index,
      }),
    ],
    [
      caseTypes,
      hasAdminAccessToPlaceholderCases,
      hasTestAccessToCarTransportTrucks,
    ]
  );

  // "Planering": en kurerad delmängd av uppdragslistorna (+ VIKTIGT-arbetslistan) som egen
  // dropdown. Pekar på samma routes som Uppdragslista-dropdownen.
  const planeringNavItems: NavRule[] = useMemo(
    () => [
      {
        content: caseTypes[CaseTypeEnum.Purchase],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.purchases.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Auction],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.auction.index,
      },
      {
        content: caseTypes[CaseTypeEnum.RentedStaff],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.rentedStaff.index,
      },
      {
        content: caseTypes[CaseTypeEnum.HomeDelivery],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.homeDeliveries.index,
      },
      {
        content: caseTypes[CaseTypeEnum.FacilityToFacility],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.facilityToFacility.index,
      },
      {
        content: caseTypes[CaseTypeEnum.FacilityTransfer],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.facilityTransfer.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Verkstadsbilar],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.verkstadsbilar.index,
      },
      {
        content: caseTypes[CaseTypeEnum.RMOther],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.rmOther.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Dackkorning],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.dackkorning.index,
      },
      {
        content: caseTypes[CaseTypeEnum.Garantiarenden],
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.garantiarenden.index,
      },
      {
        content: 'VIKTIGT',
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.viktigt.index,
      },
      {
        content: 'SKA PÅ LASTBIL',
        allow: DefaultWebAdminPolicy.allow,
        route: Routes.skaPaLastbil.index,
      },
    ],
    [caseTypes]
  );

  const otherNavItems: NavRule[] = useMemo(
    () => [
      {
        content: (
          <>
            Annons Strängnäs
            {(assignmentRequestsCountData?.numberOfUnhandledRequests ?? 0) >
              0 && <WavingHand />}
          </>
        ),
        allow: InternalDeliveryGroupAccessPolicy.allow,
        deny: InternalDeliveryGroupAccessPolicy.deny,
        route: Routes.internalDeliveryGroup.index,
      },
      {
        content: 'Transportledning',
        allow: DefaultWebAdminPolicy.allow,
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.transportLogistics.index,
      },
      {
        content: 'GoSupport',
        allow: DefaultWebAdminPolicy.allow,
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.goSupport.index,
      },
      {
        content: 'Efterkontroll',
        allow: [Roles.Efterkontroll, Roles.SuperAdmin],
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.afterControl.index,
      },
      {
        content: 'Autogodkännande-analys',
        allow: [Roles.SuperAdmin],
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.autoApproveAnalytics.index,
      },
      {
        content: 'Kvalitet (BETA)',
        allow: QualityFlagsAccessPolicy.allow,
        deny: QualityFlagsAccessPolicy.deny,
        route: Routes.quality.index,
      },
      {
        content: 'HR',
        allow: [Roles.HR, Roles.SuperAdmin, Roles.KvalitetOUtbildning],
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.hr.index,
      },
      {
        content: (
          <>
            Uppdateringar {rideUpdatesCount > 0 ? `(${rideUpdatesCount})` : ''}
          </>
        ),
        allow: DefaultWebAdminPolicy.allow,
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.rideUpdates.index,
      },
      {
        content: (
          <>
            Väntlista{' '}
            {rideWaitingListCount > 0 ? `(${rideWaitingListCount})` : ''}
          </>
        ),
        allow: DefaultWebAdminPolicy.allow,
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.rideWaitingList.index,
      },
      {
        content: (
          <>
            Förfrågningar{' '}
            {caseAssignmentRequestsCount
              ? caseAssignmentRequestsCount > 0
                ? `(${caseAssignmentRequestsCount})`
                : ''
              : ''}
            {/* `count && …` ritade ut en nolla när det inte fanns några förfrågningar. */}
            {(caseAssignmentRequestsCount ?? 0) > 0 && <WavingHand />}
          </>
        ),
        allow: DefaultWebAdminPolicy.allow,
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.caseAssignmentRequests.index,
      },
      {
        content: 'Underlag',
        allow: DefaultWebAdminPolicy.allow,
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.excelExports.index,
      },
      {
        content: 'Ekonomi',
        allow: [Roles.SuperAdmin, Roles.Ekonomi],
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.ekonomi.index,
      },
      {
        content: (
          <>
            Personal {unhandledTotalCount > 0 ? `(${unhandledTotalCount})` : ''}
          </>
        ),
        allow: EmployeesPageAccessPolicy.allow,
        deny: EmployeesPageAccessPolicy.deny,
        route: isAvailabilityReadOnly(me?.roles)
          ? Routes.employees.availability
          : Routes.employees.index,
      },
      {
        content: 'Adresser',
        allow: DefaultWebAdminPolicy.allow,
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.companies.index,
      },
      {
        content: 'Karta',
        allow: DefaultWebAdminPolicy.allow,
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.map.index,
      },
      {
        content: 'Nyckeltal',
        allow: KPIsAccessPolicy.allow,
        deny: KPIsAccessPolicy.deny,
        route: Routes.kpis.index,
      },
      {
        content: 'RM Nyckeltal',
        allow: RiddermarkKPIsAccessPolicy.allow,
        deny: RiddermarkKPIsAccessPolicy.deny,
        route: Routes.kpis.riddermark,
      },
      // Ledningsrapporten ligger bakom en egen användarlista (GlobalSettingEnum 99). RM-nyckeltal
      // gör det INTE: den vyn bär fyra widgetar som inte har med rapporten att göra, och rollen
      // RiddermarkLedning räcker för dem. Det är rapportens sju widgetar inne i vyn som flaggan
      // styr, inte vyn själv — se pages/KPIs/RiddermarkKPIs/index.tsx.
      ...(hasAccessToRiddermarkReporting
        ? [
            {
              content: 'Ledningsrapport',
              allow: ManagementReportAccessPolicy.allow,
              deny: ManagementReportAccessPolicy.deny,
              route: Routes.kpis.managementReport,
            },
          ]
        : []),
      // Sharebox-piloten: menyposten finns bara för användare i pilotlistan, så
      // funktionen är osynlig tills den slås på explicit.
      ...(hasAccessToSharebox
        ? [
            {
              content: 'Sharebox (BETA)',
              allow: DefaultWebAdminPolicy.allow,
              deny: DefaultWebAdminPolicy.deny,
              route: Routes.sharebox.index,
            },
          ]
        : []),
      {
        content: 'Inställningar',
        allow: DefaultWebAdminPolicy.allow,
        deny: DefaultWebAdminPolicy.deny,
        route: Routes.settings.index,
      },
    ],
    [
      unhandledTotalCount,
      rideUpdatesCount,
      rideWaitingListCount,
      caseAssignmentRequestsCount,
      assignmentRequestsCountData,
      hasAccessToSharebox,
      hasAccessToRiddermarkReporting,
      me?.roles,
    ]
  );

  const makeActiveClassname = useCallback(
    (props: { isActive: boolean }): string => {
      return props.isActive
        ? `${navLinkClassName} ${activeNavLinkClassName}`
        : isAuthenticated
          ? navLinkClassName
          : unAuthenticatedNavLinkClassName;
    },
    [isAuthenticated]
  );

  return (
    <NavBarContainer>
      <Img
        alt="logo"
        height={29}
        onClick={(eve) => {
          if (eve.ctrlKey || eve.altKey)
            setDevMenuState((s) => ({ ...s, showMenu: true }));
        }}
        src={`${process.env.PUBLIC_URL}/Zakra_Logo.svg`}
        width={140}
      />

      {isAuthenticated && (
        <>
          {navbarState === NavBarState.Horizontal ? (
            <WrappingNavLinks>
              {[
                ...(isAvailabilityReadOnly(me?.roles) || isStartklarOnly(me?.roles)
                  ? []
                  : assignmentNavItems),
                ...otherNavItems,
              ].map((rule) => (
                <AuthorizedNavLink
                  key={rule.route}
                  className={makeActiveClassname}
                  to={rule.route}
                  allow={rule.allow}
                  deny={rule.deny}
                >
                  {rule.content}
                </AuthorizedNavLink>
              ))}
            </WrappingNavLinks>
          ) : (
            <>
              {!isAvailabilityReadOnly(me?.roles) &&
                !isStartklarOnly(me?.roles) && (
                  <NavBarDropDown
                    navItems={assignmentNavItems}
                    makeActiveClassname={makeActiveClassname}
                  />
                )}
              {!isAvailabilityReadOnly(me?.roles) &&
                !isStartklarOnly(me?.roles) && (
                  <NavBarDropDown
                    navItems={planeringNavItems}
                    makeActiveClassname={makeActiveClassname}
                    label={{ header: 'Planering', activePrefix: 'Planering' }}
                    reflectActiveRoute={false}
                  />
                )}
              <OverflowNavLinks
                makeActiveClassname={makeActiveClassname}
                rules={otherNavItems}
              />
            </>
          )}

          <RightHandNavBar />
        </>
      )}
    </NavBarContainer>
  );
};

export default NavBar;
